import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, UserCog } from "lucide-react";

/**
 * Yellow strip that pins to the top of the app whenever a super_admin is
 * either (a) toggled "View as Org Admin" (client-only role flip) or
 * (b) actively impersonating a specific user via the backend impersonation
 * flow. Both cases give a one-click escape hatch.
 */
export default function ImpersonationBanner() {
  const {
    viewAsOrgAdmin, setViewAsOrgAdmin, isRealSuperAdmin,
    user, stopImpersonating,
  } = useAuth();
  const nav = useNavigate();
  const impersonatingUser = !!user?.impersonated_by;

  if (impersonatingUser) {
    const stop = async () => {
      try {
        await stopImpersonating();
        toast.success("Returned to your Super Admin session");
        nav("/platform/users");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not stop impersonating");
      }
    };
    return (
      <div
        data-testid="impersonation-banner-user"
        className="sticky top-0 z-50 w-full bg-amber-500 text-black px-4 py-1.5 text-xs font-semibold flex items-center justify-center gap-3 border-b border-amber-600"
      >
        <UserCog className="w-3.5 h-3.5" />
        <span>
          Impersonating <strong>{user.name || user.email}</strong>
          <span className="opacity-70"> ({user.role?.replace("_", " ")})</span>
          <span className="opacity-70"> — every screen looks exactly like theirs.</span>
        </span>
        <button
          data-testid="stop-impersonation-btn"
          onClick={stop}
          className="underline hover:no-underline"
        >
          Stop impersonating
        </button>
      </div>
    );
  }

  if (!isRealSuperAdmin || !viewAsOrgAdmin) return null;
  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 w-full bg-amber-500 text-black px-4 py-1.5 text-xs font-semibold flex items-center justify-center gap-3 border-b border-amber-600"
    >
      <Eye className="w-3.5 h-3.5" />
      <span>Viewing as <strong>Org Admin</strong> — this is exactly what your client sees.</span>
      <button
        data-testid="exit-view-as-btn"
        onClick={() => setViewAsOrgAdmin(false)}
        className="underline hover:no-underline"
      >
        Exit view-as
      </button>
    </div>
  );
}
